import { Injectable, inject } from '@angular/core';
import { Observable, defer, of } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { environment } from '@environ/environment';
import { RequestManagerService, RequestPriority } from '@app/core/cache/request-manager.service';

export interface OttoTokenResponse {
  access_token: string;
  refresh_token: string;
  api_url: string;
  expires_in: number;
}

/**
 * Otto token service. Fetches and caches tokens per project.
 * Cache emits async (delay 0) to avoid Angular change detection issues.
 */
@Injectable({ providedIn: 'root' })
export class OttoIntegrationService {
  private readonly requestManager = inject(RequestManagerService);
  private readonly baseUrl = `${environment.apiUrl}/api/otto`;
  private readonly cache = new Map<string, OttoTokenResponse>();

  getToken(projectId: number): Observable<OttoTokenResponse> {
    const cached = this.cache.get(String(projectId));
    if (cached) {
      return of(cached).pipe(delay(0));
    }
    return defer(() => this.requestManager.execute<OttoTokenResponse>({
      method: 'POST',
      url: `${this.baseUrl}/token-exchange/`,
      body: { project_id: projectId },
      priority: RequestPriority.CRITICAL,
      retries: 0,
    }))
      .pipe(
        map((r) => ({ ...r, api_url: environment.production ? r.api_url : ((environment as { ottoApiUrl?: string }).ottoApiUrl || r.api_url) })),
        tap((r) => this.cache.set(String(projectId), r))
      );
  }

  refreshToken(refreshToken: string, projectId: number): Observable<OttoTokenResponse> {
    return defer(() => this.requestManager.execute<OttoTokenResponse>({
      method: 'POST',
      url: `${this.baseUrl}/token-refresh/`,
      body: {
        refresh_token: refreshToken,
        project_id: projectId,
      },
      priority: RequestPriority.CRITICAL,
      retries: 0,
    }))
      .pipe(
        tap((r) => {
          const cached = this.cache.get(String(projectId));
          if (cached) cached.access_token = r.access_token;
        })
      );
  }

  clearCache(projectId?: number): void {
    projectId !== undefined ? this.cache.delete(String(projectId)) : this.cache.clear();
  }
}
